import { useState } from 'react'
import { createPortal } from 'react-dom'
import { api } from '../../../api'
import { ORANGE, VERT, eur, fmtDate } from './paniersProd'

/**
 * Piece COM TOURNEES en plein ecran (proto v7.3, famille FactureCom /
 * facturation-v3 BO). Difference fondamentale avec le Marche : la commission
 * est RETENUE A LA SOURCE par compensation — le producteur n'a RIEN a payer.
 * Le NET QUI LUI REVIENT est le heros. Donnees 100 % snapshot (jamais recalcule).
 * Badge : jamais « A regler » — En sequestre / Versee / Creditee wallet.
 */

// Telechargement compatible Safari/iOS (CONVENTIONS)
const telechargerBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 4000)
}

function Perfo() {
  return (
    <div className="relative" style={{ height: 22, margin: '0 -18px' }}>
      <div className="absolute" style={{ left: 26, right: 26, top: '50%', borderTop: '2px dashed rgba(7,62,61,0.18)' }} />
      <span className="absolute rounded-full" style={{ left: -11, top: 0, width: 22, height: 22, background: 'rgba(4,25,24,0.55)' }} />
      <span className="absolute rounded-full" style={{ right: -11, top: 0, width: 22, height: 22, background: 'rgba(4,25,24,0.55)' }} />
    </div>
  )
}

function FactureComTournee({ piece, versementMode, enSequestre, onClose, onToast }) {
  const [pdfLoading, setPdfLoading] = useState(false)
  if (!piece) return null

  const versee = !!piece.paidOutAt || (!enSequestre && versementMode !== 'WALLET')
  const badge = enSequestre
    ? { l: '● En séquestre', c: '#ffc9a3' }
    : versementMode === 'WALLET'
      ? { l: '● Créditée wallet', c: '#7ee8d8' }
      : { l: '● Versée', c: '#7ee8d8' }

  const pdf = async () => {
    setPdfLoading(true)
    try {
      const blob = await api.producer.downloadModuleInvoicePdf(piece.id)
      telechargerBlob(blob, `${piece.numero}.pdf`)
    } catch (e) {
      onToast?.(e.message || 'Le téléchargement du PDF a échoué.')
    } finally {
      setPdfLoading(false)
    }
  }

  return createPortal(
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[70] flex items-center justify-center"
      style={{ background: 'rgba(4,25,24,0.55)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', padding: '20px 14px' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full overflow-y-auto overscroll-contain"
        style={{ maxWidth: 420, maxHeight: '88dvh', borderRadius: 24, background: '#fdf9f4', boxShadow: '0 24px 70px rgba(0,0,0,0.35)' }}
      >
        {/* En-tete vert degrade */}
        <div className="relative overflow-hidden" style={{ background: 'linear-gradient(140deg, rgb(var(--c-green)), #0a5a54)', padding: '20px 18px 18px', color: 'rgb(var(--c-creme))' }}>
          <div className="absolute rounded-full pointer-events-none" style={{ top: -50, right: -40, width: 160, height: 160, background: 'radial-gradient(circle, rgba(20,184,166,0.35), transparent 70%)' }} />
          <div className="relative flex justify-between items-start">
            <div>
              <p className="text-[10px] font-bold uppercase" style={{ letterSpacing: 2.5, opacity: 0.7 }}>Commission & versement Paniers</p>
              <p className="font-serif text-[26px] mt-0.5">{piece.numero}</p>
              <p className="text-[11.5px] mt-1" style={{ opacity: 0.75 }}>Émise le {fmtDate(piece.dateEmission)}</p>
            </div>
            <span className="rounded-full text-[11px] font-extrabold" style={{ padding: '5px 11px', background: 'rgba(255,255,255,0.14)', color: badge.c }}>
              {badge.l}
            </span>
          </div>
        </div>

        <div style={{ padding: '16px 18px 20px' }}>
          {/* Emetteur / destinataire */}
          <div className="flex gap-2.5 mb-3.5">
            {[
              ['Émetteur', piece.emetteurNom || 'Manrina', 'Martinique'],
              ['Destinataire', piece.destinataireNom || '—', piece.destinataireAdresse || ''],
            ].map(([t, n, l]) => (
              <div key={t} className="flex-1 rounded-[14px]" style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(7,62,61,0.08)', padding: '10px 12px' }}>
                <p className="text-[9.5px] font-extrabold uppercase" style={{ letterSpacing: 1.5, color: 'rgba(7,62,61,0.45)' }}>{t}</p>
                <p className="text-[12.5px] font-bold mt-0.5" style={{ color: VERT }}>{n}</p>
                <p className="text-[10.5px] truncate text-ink/50">{l}</p>
              </div>
            ))}
          </div>

          {/* Vos ventes sur la tournee (lignes du snapshot) */}
          <div className="rounded-2xl" style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(7,62,61,0.08)', padding: '13px 15px' }}>
            <p className="text-[9.5px] font-extrabold uppercase mb-1.5" style={{ letterSpacing: 1.5, color: 'rgba(7,62,61,0.45)' }}>
              Vos ventes sur la tournée
            </p>
            {(piece.lines || []).map((l, i) => (
              <div key={i} className="flex justify-between py-[3px] gap-2">
                <span className="text-[11px] text-ink/65 truncate">{l.description} · {Number(l.quantite)} {l.unite}</span>
                <span className="font-serif text-[12px] whitespace-nowrap" style={{ color: VERT }}>{eur(l.montantHT)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-1.5 mt-1" style={{ borderTop: '1px solid rgba(7,62,61,0.07)' }}>
              <span className="text-xs font-bold text-ink/70">Ventes brutes (prix client)</span>
              <span className="font-serif text-[13px]" style={{ color: VERT }}>{eur(piece.grossSalesBase)}</span>
            </div>
            {[
              [`Commission Manrina${piece.commissionRate ? ` (${Number(piece.commissionRate)} %)` : ''} HT`, piece.montantHT],
              ['TVA 8,5 % (sur la commission seule)', piece.montantTVA],
              ['Commission TTC retenue à la source', piece.montantTTC],
            ].map(([l, v]) => (
              <div key={l} className="flex justify-between py-[4px]">
                <span className="text-xs text-ink/65">{l}</span>
                <span className="font-serif text-[13px]" style={{ color: VERT }}>− {eur(v)}</span>
              </div>
            ))}
            <div className="flex justify-between pt-2 mt-1" style={{ borderTop: '1.5px solid rgba(7,62,61,0.14)' }}>
              <span className="text-[13.5px] font-extrabold" style={{ color: VERT }}>Net qui vous revient</span>
              <span className="font-serif text-lg font-bold" style={{ color: '#0d6b5f' }}>{eur(piece.netPayout)}</span>
            </div>
          </div>

          <Perfo />

          {/* Bloc compensation — RIEN a payer */}
          <div className="rounded-[14px]" style={{ background: 'rgba(20,184,166,0.09)', border: '1px solid rgba(20,184,166,0.25)', padding: '11px 13px' }}>
            <p className="text-xs font-bold leading-relaxed" style={{ color: '#0d6b5f' }}>
              Réglée par compensation sur le reversement : {eur(piece.grossSalesBase)} − {eur(piece.montantTTC)} = {eur(piece.netPayout)}.
              Vous n'avez <strong>rien à payer</strong>.
              {enSequestre
                ? ' Le net est en séquestre le temps de la fenêtre d’ajustement.'
                : versementMode === 'WALLET'
                  ? ' Le net est crédité sur votre wallet — utilisable dans Lyannaj.'
                  : versee ? ' Le net a été versé sur votre compte.' : ''}
            </p>
          </div>

          {/* Pied charte + PDF */}
          <p className="text-[10.5px] leading-relaxed text-ink/50" style={{ margin: '12px 2px' }}>
            Commission en dedans : le prix payé par le client est construit à partir de votre net — votre prix est sanctuarisé,
            la commission ne le rogne jamais.
          </p>
          <button
            onClick={pdf}
            disabled={pdfLoading}
            className="w-full rounded-2xl text-sm font-extrabold text-white disabled:opacity-60"
            style={{ padding: 14, border: 'none', background: `linear-gradient(135deg, ${ORANGE}, #e8703a)`, boxShadow: '0 8px 24px rgba(244,137,83,0.4)' }}
          >
            {pdfLoading ? 'Préparation…' : '📄 Télécharger le PDF'}
          </button>
          <button
            onClick={onClose}
            className="w-full mt-2 rounded-2xl text-[13px] font-bold"
            style={{ padding: 12, border: 'none', background: 'rgba(7,62,61,0.06)', color: VERT }}
          >
            Fermer
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

export default FactureComTournee
